import { Box, Button, Flex, HStack, Skeleton, SkeletonText, useDisclosure, VStack, Text, ModalContent, Modal, ModalOverlay, ModalHeader, ModalBody, ModalFooter } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { IBillingLocationReport } from '../../api/types'
import { ConsumedEnergyIcon } from '../../../../assets/icons/ConsumedEnergy'
import { KILOWATT_HOUR } from '../../../../utils/consts'
import { Card } from './EnergyReportComponents/CardComponent'
import CostForConsumptionIcon from '../../../../assets/icons/CostConsumptionIcon'
import { DevicesBreakdown } from './BillingReport/DevicesBreakdown'
import { scrollBarStyle } from '../../../../utils/helpers'
import html2canvas from 'html2canvas'
import { Thumbnail } from './BillingReport/BillingReportThumbnail'
import BookmarkReport from './BookmarkReport'
import RecurringReports from './RecurringReports'
import SendEmail from './SendEmail'
import { BillingReportModal } from './BillingReport/BillingReportModal'
import { getLocationsPercentages } from './EnergyConsumptionReportPdf'
import ExportCsv from './ExportCsv'

type Props = {
  fromDate: Date
  toDate: Date
  data: IBillingLocationReport[]
  isLoading: boolean
  reportName: string
  isDownloading: boolean
  price: number
  formData: any
  handleDownload: () => void
  handleGenerate: () => void
}

function getBase64FileSizeInKB(base64: string): number {
  const base64Data = base64.split(',')[1]
  if (!base64Data) return 0

  const padding = (base64Data.match(/=+$/) || [''])[0].length
  const sizeInBytes = (base64Data.length * 3) / 4 - padding

  return sizeInBytes / 1024
}

export const BillingReportPdf = ({
  data,
  formData,
  fromDate,
  toDate,
  reportName,
  isDownloading,
  isLoading,
  price,
  handleDownload,
  handleGenerate
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [thumbnail, setThumbnail] = useState<string | null>(null)
  const [fileSize, setFileSize] = useState<string | null>(null)
  const hiddenRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null);

  const isDataEmpty = !data || data.length === 0
  const loading = isLoading || isDataEmpty

  useEffect(() => {
    let tries = 0;
    const maxTries = 10;
    const interval = 100;

    const waitForRender = setInterval(() => {
      const messageBoxes = document.querySelectorAll('[data-location]');
      const arrowIcons = document.querySelectorAll('.arrowIconThumbnailClass');

      if (messageBoxes.length > 0 || tries >= maxTries) {
        clearInterval(waitForRender);

        messageBoxes.forEach((box) => {
          (box as HTMLElement).style.transform = 'translate(0%, -30%)';
        });
        arrowIcons.forEach((icon) => {
          (icon as HTMLElement).style.transform = 'translate(0%, 12%)';
        });

        if (hiddenRef.current) {
          html2canvas(hiddenRef.current, {
            useCORS: true,
            scale: 10,
          }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            setThumbnail(imgData);

            const sizeKB = getBase64FileSizeInKB(imgData);
            setFileSize(sizeKB.toFixed(2));
          });
        }
      }

      tries++;
    }, interval);

    return () => clearInterval(waitForRender);
  }, [data]);

  const totalStats = !loading
    ? data.reduce(
      (acc, loc) => {
        acc.totalConsumption += loc.value
        acc.prevConsumption += loc.previous_value
        acc.totalGeneratedPower += loc.generated_value
        acc.prevGeneration += loc.generated_previous_value
        acc.consumptionCost += loc.total_cost
        acc.prevCost += loc.previous_cost
        acc.generatedIncome += loc.generated_total_cost
        acc.prevIncome += loc.generated_previous_cost
        return acc
      },
      {
        totalConsumption: 0,
        prevConsumption: 0,
        totalGeneratedPower: 0,
        prevGeneration: 0,
        consumptionCost: 0,
        prevCost: 0,
        generatedIncome: 0,
        prevIncome: 0,
      }
    )
    : null

  const renderThumbnail = () => (
    <Box
      ref={hiddenRef}
      position="absolute"
      top="-9999px"
      left="-9999px"
      width="794px"
      height="1123px"
    >
      <Thumbnail
        data={data}
        displayData={totalStats}
        startDate={fromDate}
        endDate={toDate}
      />
    </Box>
  )

  const renderModalContent = (ref?: React.Ref<HTMLDivElement>) => (
    <ModalContent
      ref={ref}
      borderRadius="24px"
      maxW={840}
      height={800}
      maxH={800}
      overflow="hidden auto"
      sx={scrollBarStyle}
    >
      <ModalHeader position="sticky" top={0} zIndex={1} bg="white">
        Preview
      </ModalHeader>
      <ModalBody>
        <BillingReportModal
          locations={data}
          displayData={totalStats}
          startDate={fromDate}
          endDate={toDate}
          isDownloading={false}
        />
      </ModalBody>
      <ModalFooter position="sticky" bottom={0} zIndex={1} bg="white" gap={3}>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button colorScheme="blue" isLoading={isDownloading} onClick={handleDownload}>Download</Button>
      </ModalFooter>
    </ModalContent>
  )

  return (
    <Box mt={4}>
      <Flex direction={{ base: 'column', desktopLg: 'row' }} gap={8}>
        <Box flex="1" fontSize={20} fontWeight={700}>
          <Flex gap={3}>
            {loading ? (
              <Box width="100%" borderRadius="xl" p={4} bg="white" boxShadow="sm">
                <Flex justify="space-between" align="center" mb={3}>
                  <Skeleton height="14px" width="50%" borderRadius="md" />
                  <Skeleton boxSize="24px" borderRadius="full" />
                </Flex>
                <Skeleton height="14px" width="70%" borderRadius="md" />
                <SkeletonText mt={4} noOfLines={2} spacing="3" skeletonHeight="4" />
              </Box>
            ) : (
              <Card
                width="100%"
                title="Total consumed energy"
                measurementType={KILOWATT_HOUR}
                value={totalStats.totalConsumption}
                percentageChange={getLocationsPercentages(totalStats.totalConsumption, totalStats.prevConsumption)}
              >
                <ConsumedEnergyIcon boxSize="32px" color="text.secondary" />
              </Card>
            )}
            {loading ? (
              <Box width="100%" borderRadius="xl" p={4} bg="white" boxShadow="sm">
                <Flex justify="space-between" align="center" mb={3}>
                  <Skeleton height="14px" width="50%" borderRadius="md" />
                  <Skeleton boxSize="24px" borderRadius="full" />
                </Flex>
                <Skeleton height="14px" width="70%" borderRadius="md" />
                <SkeletonText mt={4} noOfLines={2} spacing="3" skeletonHeight="4" />
              </Box>
            ) : (
              <Card
                width="100%"
                title="Cost for consumed energy"
                measurementType="лв."
                value={totalStats.consumptionCost}
                percentageChange={getLocationsPercentages(totalStats.consumptionCost, totalStats.prevCost)}
              >
                <CostForConsumptionIcon boxSize="32px" color="text.secondary" />
              </Card>
            )}
          </Flex>
          {loading ? (
            <Box
              rounded="xl"
              bg="white"
              px="20px"
              py="16px"
              boxShadow="sm"
              mt={5}
            >
              <Flex align="center" paddingLeft={3} mb={3}>
                <Skeleton height="14px" width="80px" borderRadius="md" mr={3} />
                <Skeleton height="56px" width="50%" borderRadius="md" mr={3} />
                <Flex direction="column" alignItems="center" justifyContent="center">
                  <Skeleton height="18px" width="40px" borderRadius="md" mb={1} />
                  <Skeleton height="14px" width="80px" borderRadius="md" />
                </Flex>
              </Flex>
            </Box>
          ) : (
            <DevicesBreakdown locations={data} />
          )}
        </Box>
        {loading ? (
          <Box
            flex={{ base: "1", desktopLg: "0 0 30%" }}
            bg="white"
            rounded="xl"
            px="6"
            py="6"
            boxShadow="sm"
            height="450px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
          >
            <VStack spacing="4" align="center" w="100%">
              <Skeleton height="200px" width="150px" borderRadius="md" />
              <Skeleton height="20px" width="70%" />
              <Skeleton height="16px" width="40%" />
            </VStack>

            <VStack spacing="3" w="100%">
              <Skeleton height="40px" width="60%" borderRadius="md" />
              <HStack spacing="4" justify="center">
                <Skeleton height="20px" width="20px" borderRadius="full" />
                <Skeleton height="20px" width="20px" borderRadius="full" />
                <Skeleton height="20px" width="20px" borderRadius="full" />
              </HStack>
            </VStack>
          </Box>
        ) : (
          <Box
            flex={{ base: "1", desktopLg: "0 0 30%" }}
            bg="white"
            rounded="xl"
            px="60px"
            position="sticky"
            py="20px"
            boxShadow="sm"
            height="450px"
          >
            <Flex direction="column" alignItems="center" justifyContent="center" gap={2}>
              <Box onClick={onOpen} width={200} height={250} overflow="hidden" borderRadius="md" boxShadow="md" cursor="pointer">
                {thumbnail ? (
                  <img src={thumbnail} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <Flex align="center" justify="center" height="100%" bg="gray.100">
                    Loading...
                  </Flex>
                )}
              </Box>

              <Flex direction="column" alignItems="center" justifyContent="center">
                <Text fontWeight={700} fontSize="16px" color="#484848">Billing Report</Text>
                <Text fontWeight={400} fontSize="13px" color="#9B9B9B">PDF • {fileSize ? `${fileSize} KB` : "Calculating..."}</Text>
              </Flex>

              <Button colorScheme="blue" onClick={handleDownload} isLoading={isDownloading}>
                Download
              </Button>

              {renderThumbnail()}
              <Flex gap={3}>
                <BookmarkReport
                  startDate={fromDate}
                  endDate={toDate}
                  disabled={false}
                  locations={formData.selectedLocations}
                  deviceGroups={formData.selectedGroups}
                  reportType={formData.selectedReportType}
                  devices={formData.selectedDevices}
                  reportName={reportName}
                  filters_type="bookmark"
                  isDownloading={false}
                  savedAs="pdf"
                />
                <RecurringReports
                  startDate={fromDate}
                  endDate={toDate}
                  disabled={false}
                  locations={formData.selectedLocations}
                  deviceGroups={formData.selectedGroups}
                  reportType={formData.selectedReportType}
                  devices={formData.selectedDevices}
                  reportName={reportName}
                  filters_type="recurring"
                  isDownloading={false}
                  savedAs={'pdf'}
                />
                <SendEmail
                  reportType="pdf"
                  reportName={reportName}
                  handleClick={handleGenerate}
                  disabled={false}
                  startDate={fromDate}
                  endDate={toDate}
                />
                <ExportCsv
                  startDate={fromDate}
                  endDate={toDate}
                  deviceIds={formData.selectedDevices}
                  reportName={reportName}
                  reportType={formData.selectedReportType}
                  ibexPrice={price}
                />
              </Flex>
              <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick>
                <ModalOverlay />
                <ModalContent
                  borderRadius="24px"
                  maxW={840}
                  height={800}
                  maxH={800}
                  overflow={'hidden auto'}
                  sx={{ ...scrollBarStyle }}
                >
                  {renderModalContent()}
                </ModalContent>
              </Modal>
              <Modal isOpen={true} onClose={() => { }} isCentered={false}>
                <Box
                  position="absolute"
                  top="-9999px"
                  left="-9999px"
                  visibility="hidden"
                  pointerEvents="none"
                  zIndex={-1}
                >
                  {renderModalContent(modalRef)}
                </Box>
              </Modal>
            </Flex>
          </Box>
        )}
      </Flex>
    </Box>
  )
}
